// Map для документов

import {
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { pick, transform, uniq } from "lodash";
import moment from "moment";
import { ReactNode, createContext, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { myDocumentsListName } from "src/api/config";
import { DrawerComponent, Modal } from "src/components/DialogComponent";
import NotifyTableWithFilters from "src/components/tables/notificationTables/NotifyTableWithFilters";
import NotifyTableWithFiltersMobile from "src/components/tables/notificationTables/NotifyTableWithFilters.mobile";
import { SearchUI } from "src/components/ui/Search";
import TGButton from "src/components/ui/TGButton";
import TGTag from "src/components/ui/TGTag";
import { useFiltersContext } from "src/contexts/FilterContext";
import { getDeclension } from "src/helpers/formatBytes";
import { useAppDispatch } from "src/hooks/redux";
import useAsyncEffect from "src/hooks/useAsyncEffect";
import { getItemsRequest } from "src/modules/spItems/api";
import { modifiedItemsState } from "src/modules/spItems/selectors";
import { UserListItem } from "src/types/main";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";
import DocumentFilters from "../components/Filters/DocumentFilters";
import { keyMap } from "../data/keyMap";
import { Keys, KeysList, RestKeys, UserKeys } from "../type";
import { SelectIcon } from "./SelectIcon";
import NotificationDocumentFiltersMobile from "./NotificationDocumentFilters.mobile";

interface SelectDocumentContextType {
  items: Map<number, NotifyMyDocumentsType>;
  addItem: (item: NotifyMyDocumentsType) => void;
  removeItem: (id: number) => void;
  addItems: (items: NotifyMyDocumentsType[]) => void;
  removeItems: (data: NotifyMyDocumentsType[]) => void;
  count: number;
  clearItems: () => void;
}

const SelectDocumentContext = createContext<SelectDocumentContextType | null>(null);

export const useSelectDocumentContext = (): SelectDocumentContextType => {
  const context = useContext(SelectDocumentContext);

  if (!context) {
    throw new Error("useSelectDocumentContext должен использоваться внутри SelectDocumentProvider");
  }

  return context;
};

interface SelectDocumentProviderProps {
  initial: Map<number, NotifyMyDocumentsType>;
  children: ReactNode;
}

const SelectDocumentProvider: React.FC<SelectDocumentProviderProps> = ({ initial, children }) => {
  const [items] = useState<Map<number, NotifyMyDocumentsType>>(initial);

  const [count, setCount] = useState<number>(items.size);

  const addItem = (item: NotifyMyDocumentsType) => {
    items.set(item.id, item);

    setCount(items.size);
  };

  const addItems = (data: NotifyMyDocumentsType[]) => {
    data.forEach((d) => items.set(d.id, d));

    setCount(items.size);
  };

  const removeItem = (id: number) => {
    if (items.has(id)) {
      items.delete(id);
    }

    setCount(items.size);
  };

  const removeItems = (data: NotifyMyDocumentsType[]) => {
    if (!data.length) items.clear();

    data.forEach((d) => items.delete(d.id));

    setCount(items.size);
  };

  const clearItems = () => {
    setCount(0);
    items.clear();
  };

  const contextValue: SelectDocumentContextType = {
    items,
    addItem,
    removeItem,
    addItems,
    removeItems,
    count,
    clearItems,
  };

  return <SelectDocumentContext.Provider value={contextValue}>{children}</SelectDocumentContext.Provider>;
};

function SelectDocumentsWrapper({
  submit,
  read = false,
  readInitial = [],
  zIndex,
  isMobileView = false,
}: {
  submit: (items: Map<number, NotifyMyDocumentsType>) => void;
  read?: boolean;
  readInitial?: NotifyMyDocumentsType[];
  zIndex?: number;
  isMobileView?: boolean;
}) {
  const { items, count, clearItems, addItems } = useSelectDocumentContext();

  const [openDrawer, setOpenDrawer] = useState(false);
  const [documents, setDocuments] = useState<NotifyMyDocumentsType[]>([...items.values()]);
  const [openConfirmDocumentsDialog, setOpenConfirmDocumentsDialog] = useState(false);

  const isEmpty = !count;

  function toggleDrawer(state: boolean) {
    setOpenDrawer(state);
  }

  function BackButton() {
    clearItems();
    addItems(documents);

    toggleDrawer(false);
  }

  function submitDocuments() {
    const temp = [...items.values()];
    submit(items);
    setDocuments(temp);
    setOpenDrawer(false);
  }

  return (
    <FlexRow
      className={twMerge(
        "items-center justify-between flex-nowrap",
        isMobileView ? "p-5" : "p-10",
      )}
    >
      <FlexCol className="grow">
        <FlexRow
          className={twMerge(
            isEmpty ? "" : "text-black",
            "items-center justify-between font-medium",
          )}
        >
          <FlexCol>
            {isMobileView ? (
              <div
                className={twMerge(
                  count > 100 ? "text-[#BA0C2F]" : "",
                  "shrink-0 text-[18px] leading-[28px]",
                )}
              >
                <span>Документы:</span>
                <span className="block">
                  {isEmpty ? "не выбраны" : "выбрано " + count}
                </span>
              </div>
            ) : (
              <div
                className={twMerge(
                  count > 100 ? "text-[#BA0C2F]" : "",
                  "shrink-0 text-[18px]",
                )}
              >
                Документы: {isEmpty ? "не выбраны" : "выбрано " + count}
              </div>
            )}
          </FlexCol>
          {!read && (
            <FlexCol className="shrink-0">
              <button onClick={() => toggleDrawer(true)}>
                <SelectIcon isSelect={!isEmpty} />
              </button>
            </FlexCol>
          )}
        </FlexRow>
        {documents.length > 0 && (
          <DocumentsWithShowMore
            documents={documents}
            isMobileView={isMobileView}
          />
        )}
      </FlexCol>
      {!read && (
        <DrawerComponent zIndex={zIndex} percentage="87" open={openDrawer} toggleDrawer={toggleDrawer}>
          {isMobileView ? (
            <>
              <header className="border-b px-6 pt-5 pb-4 text-black">
                <h1>Выберите документы</h1>
                <div
                  className={twMerge(
                    "mt-2 text-[16px] leading-[24px]",
                    count > 100 ? "text-[#BA0C2F]" : "text-[#637381]",
                  )}
                >
                  Выбрано: {count}
                </div>
              </header>
              <FlexCol className="max-h-[calc(100dvh-206px)] overflow-y-auto">
                <ContentArea isMobileView={true} />
              </FlexCol>
              <footer className="flex flex-col gap-3 border-t p-6 text-black shrink-0">
                <TGButton
                  disabled={!count}
                  color="bluedark"
                  className="h-[44px] w-full justify-center text-[14px]"
                  onClick={() =>
                    count > 100
                      ? setOpenConfirmDocumentsDialog(true)
                      : submitDocuments()
                  }
                >
                  Выбрать документы
                </TGButton>
                <TGButton
                  onClick={BackButton}
                  className="h-[44px] w-full justify-center text-[14px]"
                >
                  Отмена
                </TGButton>
              </footer>
            </>
          ) : (
            <>
              <header className="flex flex-row items-center px-10 pt-10 pb-8 space-x-5 text-black border-b">
                <FlexCol>
                  <h1>Выберите документы</h1>
                </FlexCol>
                <FlexCol>
                  <TGTag
                    className={twMerge(count > 0 ? "text-black" : "text-[#a0a3a9]", "translate-y-1")}
                    color={count > 100 ? "red" : "gray"}
                  >
                    Выбрано: {count}
                  </TGTag>
                </FlexCol>
              </header>
              <FlexRow className="max-h-[calc(100vh-242px)]">
                <FlexCol className="bg-white sticky-custom border-r w-[320px] text-[14px] text-[#8899A8] font-medium relative scrollbar scrollbar-w-2 scrollbar-thumb-rounded-md scrollbar-thumb-[#D1D5DB] ">
                  <FlexRow className="overflow-y-auto scrollbar scrollbar-w-2 scrollbar-thumb-rounded-md scrollbar-thumb-[#D1D5DB]">
                    <DocumentFilters />
                  </FlexRow>
                </FlexCol>

                <ContentArea />
              </FlexRow>
              <footer className="flex flex-row items-center justify-between p-10 text-black border-t">
                <TGButton onClick={BackButton}>Назад</TGButton>
                <TGButton
                  disabled={!count}
                  color="bluedark"
                  onClick={() => (count > 100 ? setOpenConfirmDocumentsDialog(true) : submitDocuments())}
                >
                  Выбрать документы
                </TGButton>
              </footer>
            </>
          )}
        </DrawerComponent>
      )}
      <Modal
        zIndex={70}
        open={openConfirmDocumentsDialog}
        toggleDialog={(state) => setOpenConfirmDocumentsDialog(state)}
        modalClassName={twMerge(
          "min-h-0",
          isMobileView && "!min-h-px !w-[calc(100vw-24px)] !min-w-0",
        )}
      >
        <header className={twMerge(isMobileView ? "px-5 pb-2" : "px-10 pb-4")}>
          <h2>Подтвердите действие</h2>
        </header>

        <div className={twMerge(isMobileView ? "px-5 py-4 grow" : "px-10 py-5 grow")}>
          Вы уверены, что хотите добавить {count} {getDeclension("документ", count)}?
        </div>

        <footer
          className={twMerge(
            isMobileView
              ? "flex flex-col gap-3 px-5 py-4 border-t"
              : "flex justify-between px-10 pt-10 mt-4 border-t",
          )}
        >
          <TGButton
            onClick={() => setOpenConfirmDocumentsDialog(false)}
            className={twMerge(
              isMobileView && "h-[44px] w-full justify-center text-[14px]",
            )}
          >
            Отмена
          </TGButton>
          <TGButton
            color="bluedark"
            className={twMerge(
              isMobileView && "h-[44px] w-full justify-center text-[14px]",
            )}
            onClick={() => {
              setOpenConfirmDocumentsDialog(false);
              submitDocuments();
            }}
          >
            Да, добавить все документы
          </TGButton>
        </footer>
      </Modal>
    </FlexRow>
  );
}

function DocumentsWithShowMore({
  documents,
  isMobileView = false,
}: {
  documents: NotifyMyDocumentsType[];
  isMobileView?: boolean;
}) {
  const [showAll, setShowAll] = useState(false);

  const [query, setQuery] = useSearchParams();

  function openDocumentCard(id: number) {
    const newSearchParams = new URLSearchParams(query);

    newSearchParams.set("documentCardMobile", id.toString());

    setQuery(newSearchParams);
  }

  function toggleShowAll() {
    setShowAll((prev) => !prev);
  }

  const mappedDocuments = useMemo(() => {
    if (showAll) {
      return documents;
    } else {
      return documents.slice(0, 10);
    }
  }, [documents, showAll]);

  return (
    <>
      <FlexRow className={twMerge(isMobileView ? "mt-4" : "mt-7.5")}>
        <FlexCol className="grow gap-y-2.5">
          {mappedDocuments.map(({ code, id }) => {
            return (
              <FlexRow
                className={twMerge(
                  "px-3.5 py-2.5 bg-[#F3F4F6] rounded-lg text-[14px] cursor-pointer",
                  isMobileView ? "flex-col gap-y-1" : "gap-x-2.5",
                )}
                onClick={() => openDocumentCard(id)}
              >
                <span className="text-[#8899A8]">{code.code}</span>
                <span className="text-darkblue break-words">{code.name}</span>
              </FlexRow>
            );
          })}
          {documents.length > 10 && (
            <FlexRow className="items-end space-x-1 cursor-pointer" onClick={toggleShowAll}>
              <FlexCol className="text-xs">{showAll ? "Скрыть" : "Показать все"}</FlexCol>
              <FlexCol>
                {showAll ? (
                  <ChevronUpIcon color="#637381" width={12} height={12} />
                ) : (
                  <ChevronDownIcon color="#637381" width={12} height={12} />
                )}
              </FlexCol>
            </FlexRow>
          )}
        </FlexCol>
      </FlexRow>
    </>
  );
}

function SelectDocuments({
  initial,
  onResult,
  read,
  zIndex,
  isMobileView = false,
}: {
  initial: Map<number, NotifyMyDocumentsType>;
  onResult: (items: Map<number, NotifyMyDocumentsType>) => void;
  read?: boolean;
  zIndex?: number;
  isMobileView?: boolean;
}) {
  return (
    <SelectDocumentProvider initial={initial}>
      <SelectDocumentsWrapper
        zIndex={zIndex}
        submit={onResult}
        read={read}
        isMobileView={isMobileView}
      />
    </SelectDocumentProvider>
  );
}

export default SelectDocuments;

export type NotifyMyDocumentsType = {
  id: number;
  code: {
    code: string;
    name: string;
  };
  publicationDate: string;
};

function ContentArea({ isMobileView = false }: { isMobileView?: boolean }) {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [nextPage, setNextPage] = useState<SP.ListItemCollectionPosition | null>(null);

  const dispatch = useAppDispatch();

  const { filters } = useFiltersContext();

  const [documents, setDocuments] = useState<NotifyMyDocumentsType[]>([]);

  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [openMobileFilters, setOpenMobileFilters] = useState(false);

  function handleSearch(value: string) {
    setQuery(value);
  }

  useAsyncEffect(async () => {
    let baseFilters = pick(filters, ["collections", "departments", "sectors", "processes", "types"]);
    let restFilters = pick(filters, [
      "critical",
      "lines",
      "sap",
      "typeEquipment",
      "model",
      "manufacturer",
      "titleEquipment",
    ]);
    let userFilters = pick(filters, ["created", "responsibleForRevision"]);
    let dateFilters = pick(filters, ["dateForRevision"]);

    const configureBaseFilters: Record<Keys, number[]> = transform(
      baseFilters,
      (result: any, value: any[], key: string) => {
        let newKey = keyMap[key as KeysList];
        let newValue = value.map((v: { id: any }) => v.id);

        if (newValue.length > 0) {
          (result as any)[newKey] = newValue;
        }
      }
    );

    const userFiltersData = Object.keys(userFilters).reduce((acc, key) => {
      const newKey = keyMap[key as UserKeys];

      if (userFilters[key as UserKeys].length > 0) {
        const ids = userFilters[key as UserKeys].map((r: UserListItem) => r.email.toLowerCase());
        acc[newKey] = ids;
      }

      return acc;
    }, {} as Record<(typeof keyMap)[UserKeys], string[]>);

    const allFilters = Object.keys(restFilters).reduce(
      (acc, key) => {
        const newValue = restFilters[key as RestKeys];
        const newKey = keyMap[key as RestKeys];

        if (key === "critical") {
          if (newValue.length > 0 && newValue.length < 2) {
            acc[newKey] = Boolean(newValue[0].id);
          }
        } else {
          if (newValue.length > 0) {
            const arrayIds = newValue.map((r: { id: any }) => r.id);
            if (acc[newKey]) {
              acc[newKey] = uniq([...acc[newKey], ...arrayIds]);
            } else {
              acc[newKey] = newValue.map((r: { id: any }) => r.id);
            }
          }
        }

        return acc;
      },
      // Если запущена локалка в таблице будут выводится все документы
      { ...configureBaseFilters, ...userFiltersData, Status: [8, 3] } as Record<string, any>
    );

    if (
      dateFilters.dateForRevision.required ||
      dateFilters.dateForRevision.date.start ||
      dateFilters.dateForRevision.date.end
    ) {
      let dateFiltersData: { start: Date | null; end: Date | null }[] = [];
      const newKey = keyMap["dateForRevision"];

      if (dateFilters.dateForRevision.required) {
        allFilters["Status"] = [3];

        // dateFiltersData.push({ start: new Date(), end: moment(new Date()).add(30, "day").toDate() });
      }

      if (dateFilters.dateForRevision.date.start || dateFilters.dateForRevision.date.end) {
        dateFiltersData.push({
          start:
            typeof dateFilters.dateForRevision.date.start === "string"
              ? new Date(dateFilters.dateForRevision.date.start)
              : dateFilters.dateForRevision.date.start,
          end:
            typeof dateFilters.dateForRevision.date.end === "string"
              ? new Date(dateFilters.dateForRevision.date.end)
              : dateFilters.dateForRevision.date.end,
        });

        allFilters[newKey] = dateFiltersData;
      }
    }

    if (query !== "") {
      allFilters["Title"] = query + "*";
    }
    try {
      setLoading(true);

      if (page === 1 || nextPage === null) {
        const myDocumentItemsResponse = await getItemsRequest({
          listName: myDocumentsListName,
          filters: allFilters,
          sortOrder: { name: "Created", desc: true },
          rowLimit: 50,
        });

        const modifiedState = modifiedItemsState(myDocumentItemsResponse.items, myDocumentsListName);

        setNextPage(myDocumentItemsResponse.nextPage);

        const myDocumentsData = modifiedState.map((d) => ({
          id: d.ID,
          code: {
            code: `${d.Code}.${d.Index}`,
            name: d.Title,
          },
          publicationDate: moment(d.PublicationDate).locale("ru").format("DD.MM.YYYY"),
        }));

        setDocuments(myDocumentsData);
      } else {
        const myDocumentItemsResponse = await getItemsRequest({
          listName: myDocumentsListName,
          filters: allFilters,
          sortOrder: { name: "Created", desc: true },
          rowLimit: 50,
          nextPage: nextPage,
        });

        const modifiedState = modifiedItemsState(myDocumentItemsResponse.items, myDocumentsListName);

        setNextPage(myDocumentItemsResponse.nextPage);

        const myDocumentsData = modifiedState.map((d) => ({
          id: d.ID,
          code: {
            code: `${d.Code}.${d.Index}`,
            name: d.Title,
          },
          publicationDate: moment(d.PublicationDate).locale("ru").format("DD.MM.YYYY"),
        }));

        setDocuments((prev) => [...prev, ...myDocumentsData]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, query, page, JSON.stringify(filters)]);

  useLayoutEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [JSON.stringify(filters)]);

  return (
    <FlexCol className={twMerge("grow", isMobileView ? "w-full" : "w-[calc(100%-320px)]")}>
      <FlexRow className={twMerge(isMobileView ? "px-6 py-4" : "px-7.5 py-6")}>
        <FlexCol className="w-full">
          <FlexRow className={twMerge("flex-wrap", isMobileView ? "mb-4 gap-y-3" : "mb-5")}>
            {isMobileView ? (
              <FlexRow className="w-full items-center gap-2">
                <FlexCol className="grow">
                  <SearchUI
                    ref={searchInputRef}
                    placeholder="Найти документ"
                    defaultValue={query}
                    onClick={handleSearch}
                  />
                </FlexCol>
                <button
                  type="button"
                  onClick={() => setOpenMobileFilters(true)}
                  className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-lg border border-[#DFE4EA] bg-white text-[#637381] shadow-custom"
                >
                  <AdjustmentsHorizontalIcon className="h-5 w-5" />
                </button>
              </FlexRow>
            ) : (
              <FlexCol className="w-full">
                <SearchUI ref={searchInputRef} placeholder="Найти документ" defaultValue={query} onClick={handleSearch} />
              </FlexCol>
            )}
            <FlexCol className="w-full">
              <span className="text-[14px] text-[#4B5563] mt-1.5">Найдено документов: {documents.length}</span>
            </FlexCol>
          </FlexRow>
          {isMobileView ? (
            <NotifyTableWithFiltersMobile documents={documents} loading={loading} />
          ) : (
            <NotifyTableWithFilters documents={documents} loading={loading} />
          )}
          {!!nextPage && (
            <TGButton
              onClick={() => setPage((prev) => prev + 1)}
              color="bluedark"
              className={twMerge(isMobileView && "mt-4 w-full justify-center")}
            >
              Подгрузить еще документы
            </TGButton>
          )}
          {isMobileView && (
            <NotificationDocumentFiltersMobile
              open={openMobileFilters}
              onClose={() => setOpenMobileFilters(false)}
            />
          )}
        </FlexCol>
      </FlexRow>
    </FlexCol>
  );
}

// Выбор документов конец
